// Need to use the React-specific entry point to import createApi
import { createApi } from "@reduxjs/toolkit/query/react";
import config from "../../config/config";
import axiosBaseQuery from "./axiosBaseQuery/axiosBaseQuery";

// Define a service using a base URL and expected endpoints
export const goalsAPI = createApi({
  reducerPath: "goalsAPI",
  baseQuery: axiosBaseQuery({
    baseUrl: config?.appUrl,
  }),
  tagTypes: ["GoalsFull", "GoalsHistory", "engagement", "engagementAll"],
  keepUnusedDataFor: 900,
  endpoints: (builder) => ({
    getGoalsData: builder.query<any, GoalsAndActionPlansArgTypes>({
      query: ({ affiliateCode, aboNumber, year }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/goal/year/${year}`,
        method: "GET",
      }),
    }),
    getGoalsHistoryData: builder.query<any, GoalsHistoryArgTypes>({
      query: ({ affiliateCode, aboNumber }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/goals/history/full`,
        method: "GET",
      }),
      providesTags: (_result, _error, { affiliateCode, aboNumber }) => [
        { type: "GoalsHistory", id: `${affiliateCode}-${aboNumber}` },
      ],
    }),
    updateGoalsData: builder.mutation<any, updateGoalArgTypes>({
      query: ({ affiliateCode, aboNumber, payload }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/goal/update`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { affiliateCode, aboNumber }) => [
        { type: "GoalsFull", id: `${affiliateCode}-${aboNumber}` },
        { type: "GoalsHistory", id: `${affiliateCode}-${aboNumber}` },
      ],
    }),
    getGoalsFullData: builder.query<any, GoalsAndActionPlansArgTypes>({
      query: ({ affiliateCode, aboNumber }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/goals/current/full`,
        method: "GET",
      }),
      providesTags: (_result, _error, { affiliateCode, aboNumber }) => [
        { type: "GoalsFull", id: `${affiliateCode}-${aboNumber}` },
      ],
    }),
    createGoal: builder.mutation<any, createGoalArgTypes>({
      query: ({ affiliateCode, aboNumber, payload }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/goal/create`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { affiliateCode, aboNumber }) => [
        { type: "GoalsFull", id: `${affiliateCode}-${aboNumber}` },
      ],
    }),
    updateGoalStatus: builder.mutation<any, goalStatusUpdateArgs>({
      query: ({ affiliateCode, aboNumber, goalId, statusId }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/goal/${goalId}/update/status/${statusId}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { affiliateCode, aboNumber }) => [
        { type: "GoalsFull", id: `${affiliateCode}-${aboNumber}` },
        { type: "GoalsHistory", id: `${affiliateCode}-${aboNumber}` },
      ],
    }),
    deleteGoalAction: builder.mutation<any, GoalsActionDeleteArgsType>({

      query: ({ actionId }) => ({
        url: `/am/abo/goal/action/${actionId}`,
        method: "DELETE",
      }),
    }),
    createEngagement: builder.mutation<any, EngagementArgsType>({
      query: ({ affiliateCode, aboNumber, payload }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/goal/engagement/create`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["engagement", "engagementAll"]
    }),
    updateEngagement: builder.mutation<any, EngagementArgsType>({
      query: ({ affiliateCode, aboNumber, payload }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/goal/engagement/update`,
        method: "POST",
        data: payload,
      }),
    }),
    listAllEngagement: builder.query<any, listEngagementType>({
      query: ({ affiliateCode, aboNumber }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/goal/engagements`,
        method: "GET",
      }),
      providesTags: ["engagementAll"]
    }),
    listEngagementById: builder.query<any, listEngagementById>({
      query: ({ affiliateCode, aboNumber, goalId }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/goal/${goalId}/engagements`,
        method: "GET",
      }),
      providesTags: ["engagement"]
    }),
    deleteEngagement: builder.mutation<any, { engagementId: string | number }>({
      query: ({ engagementId }) => ({
        url: `/am/abo/goal/engagement/${engagementId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["engagement", "engagementAll"]
    }),
    downloadEngagement: builder.query<any, deleteEngagementArgsType>({ // 
      query: ({ affiliateCode, aboNumber, aboGoalEngagementDocumentId, aboGoalEngagementId }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/goal/engagement/${aboGoalEngagementId}/document/${aboGoalEngagementDocumentId}`,
        method: "GET",
      })
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetGoalsDataQuery,
  useUpdateGoalsDataMutation,
  useGetGoalsFullDataQuery,
  useCreateGoalMutation,
  useUpdateGoalStatusMutation,
  useDeleteGoalActionMutation,
  useGetGoalsHistoryDataQuery,
  useCreateEngagementMutation,
  useUpdateEngagementMutation,
  useListAllEngagementQuery,
  useListEngagementByIdQuery,
  useDeleteEngagementMutation,
  useLazyDownloadEngagementQuery
} = goalsAPI;
